import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { SurveyContext } from "../context/SurveyContext";
import { SocketContext } from "../context/SocketContext";
import { FaArrowLeft } from "react-icons/fa";

const MobileTopBar = () => {
  const { user, endSession } = useContext(AuthContext);
  const { disconnectSocket } = useContext(SocketContext);
  const { surveyToken, conditionConfig } = useContext(SurveyContext);
  const navigate = useNavigate();
  const [isSurveyButtonActive, setIsSurveyButtonActive] = useState(false);
  const [showTimerNotification, setShowTimerNotification] = useState(false);

  useEffect(() => {
    // In test mode, activate button immediately
    if (window.__studyTestMode) {
      setIsSurveyButtonActive(true);
      return;
    }
    // Read timer from study config (via window) or condition config, fallback to 10 min
    const studyMinDuration = window.__studySessionConfig?.minDurationMinutes;
    const conditionDuration = conditionConfig?.session?.durationMinutes;
    const minutes = studyMinDuration || conditionDuration || 10;
    const timer = setTimeout(() => {
      setIsSurveyButtonActive(true);
      if (window.__studyMode) {
        setShowTimerNotification(true);
      }
    }, minutes * 60 * 1000);
    return () => clearTimeout(timer);
  }, [conditionConfig]);

  if (!user) return null;

  const handleReturnToSurvey = async () => {
    // Study mode: trigger post-survey via StudyFlow callback
    if (window.__studyMode && window.__studyPlatformDone) {
      if (
        window.confirm(
          "Are you sure you want to continue to the post-study questionnaire?"
        )
      ) {
        try {
          await endSession();
        } catch (error) {
          console.error("Failed to end session:", error);
        }
        disconnectSocket();
        window.__studyPlatformDone();
      }
      return;
    }

    // Non-study mode: build redirect URL from condition config or use default
    const buildRedirectUrl = () => {
      const redirectConfig = conditionConfig?.completionRedirect;
      if (!redirectConfig?.url) {
        if (surveyToken) {
          return `https://www.soscisurvey.de/spiders/?i=${surveyToken}`;
        }
        return null;
      }
      let url = redirectConfig.url;
      if (redirectConfig.includeParticipantId !== false && surveyToken) {
        const paramName = redirectConfig.participantIdParam || "i";
        const separator = url.includes("?") ? "&" : "?";
        url = `${url}${separator}${paramName}=${surveyToken}`;
      }
      return url;
    };

    const redirectUrl = buildRedirectUrl();
    if (redirectUrl) {
      if (
        window.confirm(
          "Are you sure you want to return to the survey? You will leave this page."
        )
      ) {
        try {
          await endSession();
          disconnectSocket();
          window.location.href = redirectUrl;
        } catch (error) {
          console.error("Failed to end session:", error);
        }
      }
    } else {
      alert(
        "You were not redirected from a survey. This button is only for survey participants."
      );
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-spiders-bg-dark border-b border-spiders-border lg:hidden">
      <div className="flex items-center justify-between h-14 px-4">
        {/* Logo */}
        <button
          onClick={() => {
            navigate("/newsfeed");
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center space-x-2 group"
        >
          <div className="relative w-8 h-8">
            <svg
              viewBox="0 0 40 40"
              className="w-full h-full text-spiders-primary-400 group-hover:text-spiders-accent-400 transition-colors duration-300"
            >
              <g stroke="currentColor" fill="none" strokeWidth="1.5">
                <line x1="20" y1="5" x2="20" y2="35" opacity="0.6" />
                <line x1="5" y1="20" x2="35" y2="20" opacity="0.6" />
                <line x1="8.5" y1="8.5" x2="31.5" y2="31.5" opacity="0.6" />
                <line x1="31.5" y1="8.5" x2="8.5" y2="31.5" opacity="0.6" />
                <polygon
                  points="20,8 28,14 28,26 20,32 12,26 12,14"
                  opacity="0.8"
                />
                <polygon
                  points="20,12 24,16 24,24 20,28 16,24 16,16"
                  opacity="0.9"
                />
                <polygon
                  points="20,16 22,18 22,22 20,24 18,22 18,18"
                  fill="currentColor"
                />
              </g>
              <g fill="currentColor">
                <circle cx="26" cy="15" r="1.5" opacity="0.8" />
                <line
                  x1="24"
                  y1="14"
                  x2="28"
                  y2="16"
                  stroke="currentColor"
                  strokeWidth="0.8"
                  opacity="0.6"
                />
                <line
                  x1="24"
                  y1="16"
                  x2="28"
                  y2="14"
                  stroke="currentColor"
                  strokeWidth="0.8"
                  opacity="0.6"
                />
              </g>
            </svg>
          </div>
          <span className="text-lg font-bold text-gradient-logo">SPIDERS</span>
        </button>

        {/* Return to Survey */}
        <button
          className={`flex items-center py-1.5 px-3 rounded-lg text-sm font-medium transition-all duration-200 ${
            isSurveyButtonActive
              ? "bg-spiders-accent-600 hover:bg-spiders-accent-700 text-white"
              : "bg-spiders-bg-elevated text-spiders-text-muted cursor-not-allowed"
          }`}
          onClick={handleReturnToSurvey}
          disabled={!isSurveyButtonActive}
        >
          <FaArrowLeft className="w-3 h-3 mr-1.5" />
          Return to Survey
        </button>
      </div>
      {showTimerNotification && (
        <div className="bg-spiders-accent-600/20 border-b border-spiders-accent-500/40 px-4 py-2 flex items-center justify-between">
          <p className="text-xs text-spiders-accent-400 leading-snug">
            You can now proceed to the final questionnaire whenever you're ready.
          </p>
          <button
            onClick={() => setShowTimerNotification(false)}
            className="text-xs text-spiders-text-muted hover:text-spiders-text-secondary ml-2 flex-shrink-0"
          >
            &times;
          </button>
        </div>
      )}
    </header>
  );
};

export default MobileTopBar;
